import './styles.scss'
import image from '../../../Assets/Images/quote-modal-img.jpeg'
import { CSSTransition } from 'react-transition-group'
import { useEffect, useRef } from 'react'
import { useForm } from '../../../Hooks/useForm'
import emailjs from '@emailjs/browser'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { solid, regular } from '@fortawesome/fontawesome-svg-core/import.macro'

const templateId = process.env.REACT_APP_EMAILJS_TEMPLATE_QUOTE
const serviceId  = process.env.REACT_APP_EMAILJS_SERVICE_ID
const publicId   = process.env.REACT_APP_EMAILJS_PUBLIC_KEY

const SERVICES = [
  'Air Freight',
  'Ocean Freight',
  'Cargo Transportation',
  'Packaging & Storage',
]

const QuoteForm = ({ showForm, handleClose }) => {
  const formRef = useRef(null)

  const { form, reset, onChange } = useForm({
    name: '',
    company: '',
    email: '',
    phone: '',
    service: '',
    origin: '',
    destination: '',
    weight: '',
    date: '',
    msg: '',
    loading: false,
    success: false,
  })

  const handleChange = (e) => onChange(e.target.name, e.target.value)

  useEffect(() => {
    return reset
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleSubmit = async (e) => {
    e.preventDefault()
    onChange('loading', true)

    const { loading, success, ...payload } = form

    try {
      await emailjs.send(serviceId, templateId, payload, publicId)
      onChange('success', true)
    } catch (error) {
      console.log('ERROR', error)
    }

    onChange('loading', false)
  }

  const handleClick = (e) => e.stopPropagation()

  return (
    <CSSTransition
      in={showForm}
      nodeRef={formRef}
      timeout={300}
      unmountOnExit
      classNames="quote-form"
    >
      <div ref={formRef} className="quote-form" onClick={handleClick}>
        {/* Side image */}
        <div className="quote-form__image">
          <img src={image} alt="" />
          <div className="quote-form__image__overlay">
            <h3>Fast.<br />Reliable.<br />Worldwide.</h3>
            <p>Get a personalised quote within 24 hours.</p>
          </div>
        </div>

        {/* Form content */}
        <div className="quote-form__content">
          <div className="quote-form__header">
            <div>
              <h2 className="quote-form__header__title">Get A Free Quote</h2>
              <p className="quote-form__header__sub">Fill in the details below and we'll get back to you shortly.</p>
            </div>
            <button className="quote-form__close" onClick={handleClose} aria-label="Close">
              <FontAwesomeIcon icon={solid('xmark')} />
            </button>
          </div>

          {form.success ? (
            <div className="quote-form__success">
              <div className="quote-form__success__icon">
                <FontAwesomeIcon icon={solid('circle-check')} />
              </div>
              <h3>Quote Request Sent!</h3>
              <p>Thank you! We'll review your request and get back to you within 24 hours.</p>
              <button className="quote-form__success__back" onClick={() => { onChange('success', false); reset() }}>
                Submit another request
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              {/* Section: Contact Details */}
              <div className="quote-form__section-label">
                <FontAwesomeIcon icon={solid('user')} />
                Contact Details
              </div>

              <div className="qf-row">
                <div className="qf-field">
                  <label htmlFor="qf-name">Full Name <span>*</span></label>
                  <input id="qf-name" name="name" type="text" required placeholder="John Doe" value={form.name} onChange={handleChange} />
                </div>
                <div className="qf-field">
                  <label htmlFor="qf-company">Company Name</label>
                  <input id="qf-company" name="company" type="text" placeholder="Acme Corp (optional)" value={form.company} onChange={handleChange} />
                </div>
              </div>

              <div className="qf-row">
                <div className="qf-field">
                  <label htmlFor="qf-email">Email Address <span>*</span></label>
                  <input id="qf-email" name="email" type="email" required placeholder="john@example.com" value={form.email} onChange={handleChange} />
                </div>
                <div className="qf-field">
                  <label htmlFor="qf-phone">Phone Number <span>*</span></label>
                  <input id="qf-phone" name="phone" type="tel" required placeholder="+1 (000) 000-0000" value={form.phone} onChange={handleChange} />
                </div>
              </div>

              {/* Section: Shipment Details */}
              <div className="quote-form__section-label">
                <FontAwesomeIcon icon={solid('boxes-stacked')} />
                Shipment Details
              </div>

              <div className="qf-field">
                <label htmlFor="qf-service">Service Type <span>*</span></label>
                <select id="qf-service" name="service" required value={form.service} onChange={handleChange}>
                  <option value="" disabled>Select a service...</option>
                  {SERVICES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>

              <div className="qf-row">
                <div className="qf-field">
                  <label htmlFor="qf-origin">Origin / Pickup <span>*</span></label>
                  <input id="qf-origin" name="origin" type="text" required placeholder="City, Country" value={form.origin} onChange={handleChange} />
                </div>
                <div className="qf-field">
                  <label htmlFor="qf-destination">Destination <span>*</span></label>
                  <input id="qf-destination" name="destination" type="text" required placeholder="City, Country" value={form.destination} onChange={handleChange} />
                </div>
              </div>

              <div className="qf-row">
                <div className="qf-field">
                  <label htmlFor="qf-weight">Cargo Weight</label>
                  <input id="qf-weight" name="weight" type="text" placeholder="e.g. 500 kg" value={form.weight} onChange={handleChange} />
                </div>
                <div className="qf-field">
                  <label htmlFor="qf-date">Preferred Pickup Date</label>
                  <input id="qf-date" name="date" type="date" value={form.date} onChange={handleChange} />
                </div>
              </div>

              {/* Section: Notes */}
              <div className="quote-form__section-label">
                <FontAwesomeIcon icon={regular('comment-dots')} />
                Additional Notes
              </div>

              <div className="qf-field">
                <label htmlFor="qf-msg">Special Requirements or Notes</label>
                <textarea id="qf-msg" name="msg" rows={3} placeholder="Any special handling, dimensions, or additional information..." value={form.msg} onChange={handleChange} />
              </div>

              <button type="submit" className="quote-form__submit" disabled={form.loading}>
                {form.loading ? (
                  <><FontAwesomeIcon icon={solid('circle-notch')} spin /> Sending...</>
                ) : (
                  <><FontAwesomeIcon icon={regular('paper-plane')} /> Request My Quote</>
                )}
              </button>
            </form>
          )}
        </div>
      </div>
    </CSSTransition>
  )
}

export default QuoteForm
